import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
  Inject,
  PLATFORM_ID,
  HostListener,
  inject
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { ContactService } from '../contact.service';
import { AnalyticsService } from '../../../shared/services/analytics.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-contact-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatCardModule,
    MatSnackBarModule
  ],
  templateUrl: './contact-form.component.html',
  styleUrl: './contact-form.component.scss'
})
export class ContactFormComponent implements OnInit, AfterViewInit, OnDestroy {
  contactForm: FormGroup;
  isSubmitting = false;
  submitted = false;
  private readonly analytics = inject(AnalyticsService);
  errorMessage = '';
  readonly messageMinLength = 10;
  /** True once the Turnstile script has loaded and the widget has been rendered */
  turnstileReady = false;
  /** True once the user has completed the Turnstile challenge (callback fired) */
  turnstileVerified = false;
  /** Shown while the Turnstile script/widget is still loading */
  turnstileLoadError = false;
  private turnstileWidgetId: string | null = null;
  private turnstilePollTimer: ReturnType<typeof setInterval> | null = null;
  private turnstileVisibilityObserver: IntersectionObserver | null = null;
  private turnstileVisibilityFallback: ReturnType<typeof setTimeout> | null = null;
  private turnstileLoadScheduled = false;
  private readonly turnstilePollMaxMs = 15000;
  private readonly turnstilePollIntervalMs = 300;
  private readonly turnstileScriptId = 'cf-turnstile-api-js';
  private readonly turnstileFallbackDelayMs = 2800;

  private readonly onOnlineRetry = (): void => {
    if (this.turnstileLoadError) {
      this.retryTurnstile();
    }
  };

  private readonly onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted && this.turnstileLoadError) {
      this.retryTurnstile();
    }
  };

  get messageLength(): number {
    const v = this.contactForm.get('message')?.value;
    return typeof v === 'string' ? v.length : 0;
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly contactService: ContactService,
    private readonly snackBar: MatSnackBar,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {
    this.contactForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      organization: ['', [Validators.required, Validators.minLength(2)]],
      phone: [''],
      inquiryType: ['general', Validators.required],
      message: ['', [Validators.required, Validators.minLength(10)]]
    });
  }

  ngOnInit() {
    // Check for query parameters to pre-populate the inquiry type
    this.route.queryParams.subscribe(params => {
      const inquiryType = params['inquiryType'];
      if (inquiryType) {
        this.contactForm.patchValue({ inquiryType });
      }
    });
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('online', this.onOnlineRetry);
      window.addEventListener('pageshow', this.onPageShow);
      this.scheduleTurnstileLoadWhenVisible();
    }
  }

  ngOnDestroy() {
    this.clearTurnstilePoll();
    this.disconnectTurnstileVisibility();
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('online', this.onOnlineRetry);
      window.removeEventListener('pageshow', this.onPageShow);
    }
  }

  @HostListener('document:visibilitychange')
  onDocumentVisibility(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (document.visibilityState === 'visible' && this.turnstileLoadError) {
      this.retryTurnstile();
    }
  }

  private clearTurnstilePoll(): void {
    if (this.turnstilePollTimer !== null) {
      clearInterval(this.turnstilePollTimer);
      this.turnstilePollTimer = null;
    }
  }

  private disconnectTurnstileVisibility(): void {
    this.turnstileVisibilityObserver?.disconnect();
    this.turnstileVisibilityObserver = null;
    if (this.turnstileVisibilityFallback !== null) {
      clearTimeout(this.turnstileVisibilityFallback);
      this.turnstileVisibilityFallback = null;
    }
  }

  /**
   * Defers loading the Turnstile script until the widget area is near the viewport (or after a short
   * fallback), so the contact hero and form paint without waiting on challenges.cloudflare.com.
   */
  private scheduleTurnstileLoadWhenVisible(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const beginLoad = (): void => {
      if (this.turnstileLoadScheduled) {
        return;
      }
      this.turnstileLoadScheduled = true;
      this.disconnectTurnstileVisibility();
      requestAnimationFrame(() => {
        setTimeout(() => this.waitForTurnstileAndRender(), 0);
      });
    };

    this.turnstileVisibilityFallback = setTimeout(() => beginLoad(), this.turnstileFallbackDelayMs);

    queueMicrotask(() => {
      const el = document.getElementById('turnstile-container');
      if (!el) {
        beginLoad();
        return;
      }

      if (typeof IntersectionObserver === 'undefined') {
        beginLoad();
        return;
      }

      this.turnstileVisibilityObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            beginLoad();
          }
        },
        { root: null, rootMargin: '180px 0px 360px 0px', threshold: 0 }
      );
      this.turnstileVisibilityObserver.observe(el);
    });
  }

  /**
   * Loads Turnstile only on this route, then renders the widget.
   * Retries on tab focus / network restore via retryTurnstile() for transient DNS or idle-tab failures.
   */
  private waitForTurnstileAndRender(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.loadTurnstileScript()
      .then(() => this.pollUntilRendered())
      .catch(() => {
        this.turnstileLoadError = true;
        this.cdr.markForCheck();
      });
  }

  /** Injects challenges.cloudflare.com Turnstile script once (?render=explicit matches manual render()). */
  private loadTurnstileScript(): Promise<void> {
    const g = globalThis as any;
    if (g.turnstile !== undefined) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      let script = document.getElementById(this.turnstileScriptId) as HTMLScriptElement | null;
      if (script) {
        if (g.turnstile !== undefined) {
          resolve();
          return;
        }
        const onLoad = (): void => {
          script!.removeEventListener('load', onLoad);
          script!.removeEventListener('error', onErr);
          resolve();
        };
        const onErr = (): void => {
          script!.removeEventListener('load', onLoad);
          script!.removeEventListener('error', onErr);
          script!.remove();
          reject(new Error('Turnstile script failed'));
        };
        script.addEventListener('load', onLoad);
        script.addEventListener('error', onErr);
        return;
      }

      script = document.createElement('script');
      script.id = this.turnstileScriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = (): void => resolve();
      script.onerror = (): void => {
        script!.remove();
        reject(new Error('Turnstile script failed'));
      };
      document.head.appendChild(script);
    });
  }

  private pollUntilRendered(): void {
    const tryRender = (): boolean => {
      if ((globalThis as any).turnstile === undefined) {
        return false;
      }
      this.clearTurnstilePoll();
      this.renderTurnstile();
      return true;
    };

    if (tryRender()) {
      return;
    }

    const deadline = Date.now() + this.turnstilePollMaxMs;
    this.turnstilePollTimer = setInterval(() => {
      if (Date.now() > deadline) {
        this.clearTurnstilePoll();
        this.turnstileLoadError = true;
        this.cdr.markForCheck();
        return;
      }
      if (tryRender()) {
        this.cdr.markForCheck();
      }
    }, this.turnstilePollIntervalMs);
  }

  retryTurnstile(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.clearTurnstilePoll();
    this.disconnectTurnstileVisibility();
    this.turnstileLoadScheduled = false;
    document.getElementById(this.turnstileScriptId)?.remove();
    document.getElementById('turnstile-container')?.replaceChildren();
    this.turnstileWidgetId = null;
    this.turnstileReady = false;
    this.turnstileVerified = false;
    this.turnstileLoadError = false;
    this.cdr.markForCheck();
    this.waitForTurnstileAndRender();
  }

  private renderTurnstile(): void {
    const turnstileApi = (globalThis as any).turnstile;
    if (turnstileApi === undefined) {
      return;
    }
    const container = document.getElementById('turnstile-container');
    if (!container || this.turnstileWidgetId !== null) {
      return;
    }
    try {
      const useCompact =
        typeof window !== 'undefined' && window.matchMedia('(max-width: 576px)').matches;

      this.turnstileWidgetId = turnstileApi.render('#turnstile-container', {
        sitekey: environment.turnstileSiteKey,
        callback: (token: string) => {
          this.turnstileVerified = true;
          this.cdr.markForCheck();
        },
        'error-callback': () => {
          this.turnstileVerified = false;
          this.cdr.markForCheck();
        },
        'expired-callback': () => {
          this.turnstileVerified = false;
          this.cdr.markForCheck();
        },
        size: useCompact ? 'compact' : 'normal',
        theme: 'light'
      });
      this.turnstileReady = true;
      this.turnstileLoadError = false;
      this.cdr.markForCheck();
    } catch {
      this.turnstileWidgetId = null;
      this.turnstileLoadError = true;
      this.cdr.markForCheck();
    }
  }

  async onSubmit() {
    if (this.contactForm.valid) {
      this.isSubmitting = true;
      this.errorMessage = '';
      
      try {
        // Get Turnstile token
        const turnstileToken = await this.getTurnstileToken();
        
        // Prepare form data
        const formData = {
          ...this.contactForm.value,
          turnstileToken
        };
        
        // Submit to API
        this.contactService.submitContactForm(formData).subscribe({
          next: (response) => {
            this.isSubmitting = false;
            this.submitted = true;
            // Conversion only after the API confirms success; no PII, just form + inquiry type.
            this.analytics.trackLead('contact_form', this.contactForm.value.inquiryType);
            this.contactForm.reset();
            this.contactForm.patchValue({
              inquiryType: 'general'
            });
            this.snackBar.open('Message sent successfully!', 'Close', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['success-snackbar']
            });
          },
          error: (error) => {
            this.isSubmitting = false;
            this.handleSubmissionError(error);
          }
        });
      } catch (error: any) {
        this.isSubmitting = false;
        this.handleSecurityVerificationError(error);
      }
    } else {
      this.markFormGroupTouched();
    }
  }

  private handleSubmissionError(error: any) {
    // Determine the error message based on the error type
    let userFriendlyMessage = 'We encountered an issue sending your message. Please try again.';
    
    if (error.message) {
      if (error.message.includes('network') || error.message.includes('timeout')) {
        userFriendlyMessage = 'Network connection issue. Please check your internet connection and try again.';
      } else if (error.message.includes('server') || error.message.includes('500')) {
        userFriendlyMessage = 'Our servers are experiencing issues. Please try again in a few moments.';
      } else if (error.message.includes('validation')) {
        userFriendlyMessage = 'Please check your form inputs and try again.';
      } else {
        userFriendlyMessage = error.message;
      }
    }
    
    this.errorMessage = userFriendlyMessage;
    console.error('Contact form submission error:', error);
  }

  private handleSecurityVerificationError(error: any) {
    this.errorMessage = error?.message && !error.message.includes('not loaded')
      ? error.message
      : 'Security verification failed. Please refresh the page and try again.';
    this.turnstileVerified = false;
    this.resetTurnstileWidget();
    this.cdr.markForCheck();
    console.error('Turnstile verification error:', error);
  }

  private resetTurnstileWidget(): void {
    const turnstileApi = (globalThis as any).turnstile;
    if (turnstileApi !== undefined && this.turnstileWidgetId !== null) {
      try {
        turnstileApi.reset(this.turnstileWidgetId);
      } catch {
        // ignore
      }
    }
  }

  clearError() {
    this.errorMessage = '';
  }

  private getTurnstileToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        const turnstileApi = (globalThis as any).turnstile;
        if (turnstileApi === undefined) {
          reject(new Error('Verification is still loading. Please wait a moment and try again.'));
          return;
        }
        const token = this.turnstileWidgetId === null
          ? turnstileApi.getResponse()
          : turnstileApi.getResponse(this.turnstileWidgetId);
        if (token) {
          resolve(token);
        } else {
          reject(new Error('Please complete the verification above before sending.'));
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  private markFormGroupTouched() {
    Object.keys(this.contactForm.controls).forEach(key => {
      const control = this.contactForm.get(key);
      control?.markAsTouched();
    });
  }

  getErrorMessage(controlName: string): string {
    const control = this.contactForm.get(controlName);
    if (control?.hasError('required')) {
      return 'This field is required';
    }
    if (control?.hasError('email')) {
      return 'Please enter a valid email address';
    }
    if (control?.hasError('minlength')) {
      const requiredLength = control.getError('minlength').requiredLength;
      return `Minimum length is ${requiredLength} characters`;
    }
    return '';
  }
}
